import React from "react";
import {
  CalculatorOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, Card, Table, Tag, message, Spin } from "antd";
import {
  ClientWithStorage,
  useGetClientsWithActiveStorage,
} from "~/services/client/client.queries";
import { calculateMonthlyStorage } from "~/services/storage/storage.api";
import { PreviewData } from "../types";
import { Link } from "react-router-dom";

interface StorageSettingsTabProps {
  onPreviewInvoice: (previewData: PreviewData) => void;
  onEditSettings: (client: ClientWithStorage) => void;
}

export const StorageSettingsTab: React.FC<StorageSettingsTabProps> = ({
  onPreviewInvoice,
  onEditSettings,
}) => {
  const { data: clients, isLoading: isLoadingClients } =
    useGetClientsWithActiveStorage();
  const [isProcessing, setIsProcessing] = React.useState(false);

  // Handle preview invoice for a specific client
  const handlePreviewInvoice = async (client: ClientWithStorage) => {
    try {
      setIsProcessing(true);

      // Calculate storage for the current month
      const now = new Date();
      const response = await calculateMonthlyStorage(
        client.id,
        now.getFullYear(),
        now.getMonth() + 1
      );

      // Create preview data with detailed calculations
      const monthName = now.toLocaleString("default", { month: "long" });

      // Storage calculation based on cubic footage and rate
      const storageAmount = response.averageCubicFootage * response.storageRate;

      // Insurance calculation based on inventory value and insurance rate
      let insuranceAmount = 0;
      if (
        response.includeInsurance &&
        response.insuranceRate !== null &&
        response.totalInventoryValue
      ) {
        insuranceAmount = response.totalInventoryValue * response.insuranceRate;
      }

      // Calculate subtotal
      const subtotal = storageAmount + insuranceAmount;

      // Apply tax (8.875% as standard rate)
      const taxRate = 0.08875;
      const taxAmount = subtotal * taxRate;

      // Calculate total with tax
      const totalAmount = subtotal + taxAmount;

      const previewData: PreviewData = {
        client,
        calculation: response,
        monthName,
        year: now.getFullYear(),
        storageAmount: Math.round(storageAmount * 100) / 100,
        insuranceAmount: Math.round(insuranceAmount * 100) / 100,
        subtotal: Math.round(subtotal * 100) / 100,
        taxAmount: Math.round(taxAmount * 100) / 100,
        totalAmount: Math.round(totalAmount * 100) / 100,
      };

      onPreviewInvoice(previewData);
    } catch (error) {
      console.error("Error calculating storage:", error);
      message.error(
        error instanceof Error
          ? `Failed to calculate storage: ${error.message}`
          : "Failed to calculate storage. Please check your connection to the backend."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const clientColumns = [
    {
      title: "Client",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "Rate ($/ft³)",
      dataIndex: "storageRate",
      key: "storageRate",
      render: (rate: any) => {
        const numRate = typeof rate === "number" ? rate : parseFloat(rate) || 0;
        return `$${numRate.toFixed(2)}`;
      },
    },
    {
      title: "Insurance",
      dataIndex: "includeInsurance",
      key: "includeInsurance",
      render: (include: boolean) =>
        include ? (
          <Tag color="green">Included</Tag>
        ) : (
          <Tag color="gray">Not Included</Tag>
        ),
    },
    {
      title: "Last Invoice",
      dataIndex: "lastInvoiceDate",
      key: "lastInvoiceDate",
      render: (date: string | undefined) =>
        date ? new Date(date).toLocaleDateString() : "Never",
    },
    {
      title: "Current Month",
      key: "currentMonth",
      render: (_: any, record: ClientWithStorage) => {
        const now = new Date();
        // Check if already invoiced for current month
        const isInvoiced =
          record.lastInvoiceDate &&
          new Date(record.lastInvoiceDate).getMonth() === now.getMonth() &&
          new Date(record.lastInvoiceDate).getFullYear() === now.getFullYear();

        if (isInvoiced) {
          return <Tag color="green">Invoiced</Tag>;
        } else {
          return <Tag color="orange">Pending</Tag>;
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: ClientWithStorage) => (
        <div className="flex space-x-2">
          <Button
            type="primary"
            size="small"
            onClick={() => handlePreviewInvoice(record)}
            icon={<CalculatorOutlined />}
            loading={isProcessing}
          >
            Calculate
          </Button>
          <Button
            size="small"
            onClick={() => onEditSettings(record)}
            icon={<InfoCircleOutlined />}
          >
            Settings
          </Button>
        </div>
      ),
    },
  ];

  if (isLoadingClients) {
    return (
      <Spin size="large" className="flex justify-center items-center h-64" />
    );
  }

  return (
    <Card>
      {clients && clients.length === 0 ? (
        <div className="text-center py-8">
          <div className="mb-4">
            <WarningOutlined className="text-4xl text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Storage Clients Found
          </h3>
          <p className="text-gray-600 mb-4">
            No clients have been configured for storage services yet. Set up
            storage rates and settings for clients to enable automated storage
            invoicing.
          </p>
          <Link to="/client/list">
            <Button type="primary">Manage Clients</Button>
          </Link>
        </div>
      ) : (
        <Table
          dataSource={clients}
          columns={clientColumns}
          rowKey="id"
          pagination={false}
          loading={isLoadingClients}
        />
      )}
    </Card>
  );
};
